/**
 * QualityDimensionBar — Labeled progress bar for quality dimensions.
 *
 * UX Spec Section 5.2.7 / Story 1.6 AC#2.
 *
 * Threshold coloring: ≥90% brand green, 70–89% mid-green, 50–69% amber, <50% red.
 * Variants: standard (6px bar) and compact (4px bar).
 */

export interface QualityDimensionBarProps {
  label: string;
  value: number; // 0–100
  compact?: boolean;
}

function fillColor(value: number): string {
  // Token names come from `styles/tokens.css` — the only stylesheet
  // imported into the bundle. An earlier draft of this component
  // referenced `--readiness-warning` / `--readiness-critical`, which
  // only exist in the unused `globals.css`. With those names the
  // browser resolves `var(...)` to nothing and the bar renders as an
  // invisible track — see the Uniqueness=21% case on the supplier
  // detail panel. Use the canonical fair/poor tokens with explicit
  // hex fallbacks so a future tokens rename can't break this again.
  if (value >= 90) return "var(--readiness-excellent, #86bc25)";
  if (value >= 70) return "var(--readiness-good, #6cc24a)";
  if (value >= 50) return "var(--readiness-fair, #f59e0b)";
  return "var(--readiness-poor, #e53e3e)";
}

export function QualityDimensionBar({
  label,
  value,
  compact = false,
}: QualityDimensionBarProps) {
  const clamped = Math.min(100, Math.max(0, value));
  const color = fillColor(clamped);

  return (
    <div className="flex items-center gap-3">
      <span
        className={`${compact ? "text-caption w-24" : "text-body w-28"} text-[var(--muted-foreground)] shrink-0`}
      >
        {label}
      </span>
      <div
        className={`flex-1 rounded-full bg-[var(--muted-foreground)]/20 overflow-hidden ${compact ? "h-1" : "h-1.5"}`}
        role="meter"
        aria-valuenow={clamped}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`${label}: ${clamped}%`}
      >
        <div
          className="h-full rounded-full transition-all duration-500 ease-out"
          style={{ width: `${clamped}%`, backgroundColor: color }}
        />
      </div>
      <span
        className={`${compact ? "text-caption" : "text-body"} text-[var(--foreground)] w-10 text-right font-medium`}
      >
        {clamped}%
      </span>
    </div>
  );
}
